import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  Fade,
  Backdrop,
  IconButton
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import type { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { getCroppedImg } from "../../utils/cropImage";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "#1C1A26",
  border: "2px solid #29273A",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
  color: "#fff",
};

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

interface CompanyData {
  name: string;
  contactEmail: string;
  domain: string;
  linkedIn_url: string;
  website_url: string | null;
  type: string;
  status: "active" | "inactive";
  logoFile?: File;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: CompanyData) => void;
  defaultValues: CompanyData;
}

const CompanyBasicDetailsModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  defaultValues,
}) => {
  const [formData, setFormData] = useState<CompanyData>(defaultValues);
  const [imgSrc, setImgSrc] = useState("");
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [croppedImagePreview, setCroppedImagePreview] = useState("");

  useEffect(() => {
    setFormData(defaultValues);
  }, [defaultValues]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleInputChange = (
    key: keyof CompanyData,
    value: string | boolean | File,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: key === "status" ? (value ? "active" : "inactive") : value,
    }));
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setCrop(undefined); // Reset crop when new image is uploaded
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
        setIsCropModalOpen(true);
      });
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const handleCropComplete = async () => {
    if (completedCrop && imgRef.current) {
      const croppedFile = await getCroppedImg(imgSrc, completedCrop);
      if (croppedFile) {
        handleInputChange("logoFile", croppedFile);
        const objectUrl = URL.createObjectURL(croppedFile);
        setCroppedImagePreview(objectUrl);
      }
    }
    setIsCropModalOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={open}>
          <Box sx={style}>
            <Typography variant="h6" mb={2}>
              Edit Company Details
            </Typography>

            {[
              { label: "Company Name", key: "name" },
              { label: "Contact Email", key: "contactEmail" },
              { label: "Domain", key: "domain" },
              { label: "LinkedIn URL", key: "linkedIn_url" },
              { label: "Website URL", key: "website_url" },
            ].map(({ label, key }) => (
              <TextField
                key={key}
                fullWidth
                label={label}
                variant="outlined"
                size="small"
                value={formData[key as keyof CompanyData] || ""}
                onChange={(e) =>
                  handleInputChange(key as keyof CompanyData, e.target.value)
                }
                sx={{
                  mb: 2,
                  input: { color: "#fff" },
                  "& .MuiInputLabel-root": { color: "#ccc" },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#B8F890" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#777" },
                    "&:hover fieldset": { borderColor: "#B8F890" },
                    "&.Mui-focused fieldset": { borderColor: "#B8F890" },
                  },
                }}
              />
            ))}

            <TextField
              disabled
              fullWidth
              label="Type"
              variant="outlined"
              size="small"
              value={formData.type}
              sx={{
                mb: 2,
                input: { color: "#fff", WebkitTextFillColor: "#fff !important" },
                "& .MuiInputLabel-root": { color: "#ccc" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#777 !important" },
                },
              }}
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="#ccc" mb={1}>
                Organization Logo
              </Typography>
              
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragActive ? "#B8F890" : "#777",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: isDragActive ? "rgba(184, 248, 144, 0.1)" : "transparent",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#B8F890",
                    bgcolor: "rgba(184, 248, 144, 0.05)",
                  },
                }}
              >
                <input {...getInputProps()} />
                {croppedImagePreview ? (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <img src={croppedImagePreview} alt="Logo preview" style={{ maxHeight: 100, borderRadius: 8, marginBottom: 8 }} />
                    <Typography variant="caption" color="#B8F890">Click or drag to change logo</Typography>
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <CloudUploadIcon sx={{ fontSize: 40, color: "#ccc" }} />
                    <Typography variant="body2" color="#ccc">
                      Drag & drop a logo here, or click to select
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ borderColor: "#B8F890", color: "#B8F890" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  bgcolor: "#B8F890",
                  color: "#000",
                  "&:hover": { bgcolor: "#A4E67E" },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Crop Modal */}
      <Modal open={isCropModalOpen} onClose={() => setIsCropModalOpen(false)}>
        <Box sx={{ ...style, width: 'auto', maxWidth: '90vw' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Crop Logo</Typography>
            <IconButton onClick={() => setIsCropModalOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {!!imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                onLoad={onImageLoad}
                style={{ maxHeight: '60vh', maxWidth: '100%' }}
              />
            </ReactCrop>
          )}

          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <Button variant="outlined" onClick={() => setIsCropModalOpen(false)} sx={{ color: '#fff', borderColor: '#777' }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleCropComplete} sx={{ bgcolor: '#B8F890', color: '#000', "&:hover": { bgcolor: "#A4E67E" } }}>
              Apply Crop
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CompanyBasicDetailsModal;
